import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import TextButton from 'LuxKit/Button/TextButton'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useCreateExperienceSelectionSession from 'hooks/ExperiencePage/useCreateExperienceSelectionSession'
import {
  ARENA_SCRIPT_SRC,
  arenaEvents,
  defaultArenaOptions,
  areAllArenaSeatsMatchedToTickets,
  getArenaCustomLegend,
  getArenaDisplaySeatPrice,
  getArenaDisplayTotalPrice,
  getArenaReservationFailureReason,
  hasMixedArenaReservableSeatGroups,
  hasValidArenaReserveResult,
  getArenaEventStateUpdate,
  getTrolleyToken,
  loadArenaScript,
} from './arenaSeatSelectionUtils'
import { createArenaTrolleyToken, validateArenaTrolleyReservation } from './arenaSeatSelectionApi'
import type {
  ArenaApp,
  ArenaConfigOptions,
  ArenaCreateOptions,
  ArenaCurrency,
  ArenaErrorType,
  ArenaEventName,
  ArenaEventPayload,
  ArenaReservationFailureReason,
  ArenaSelectedSeat,
  ArenaSelectionResult,
  ArenaSendMethod,
  ArenaStatus,
} from './arenaSeatSelectionUtils'

interface ArenaErrorState {
  type: ArenaErrorType
  eventName?: ArenaEventName
  reservationFailureReason?: ArenaReservationFailureReason
}

interface Props {
  apiBase: string
  displayCurrencyCode: string
  eventId: string
  experienceId: string
  experienceName: string
  onSelectionComplete: (result: ArenaSelectionResult) => void
  perfId: string
  selectedTimeSlot: App.ExperienceAvailabilityTimeSlot
  tyrBase: string
  options?: ArenaConfigOptions
  scriptSrc?: string
}

declare global {
  interface Window {
    IngressoArena?: {
      create: (options: ArenaCreateOptions) => Promise<ArenaApp>
    }
  }
}

const Shell = styled.div`
  border-radius: ${props => props.theme.borderRadius.M};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  padding: ${rem(20)} ${rem(24)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`

const HeaderCopy = styled.div`
  display: grid;
  gap: ${rem(4)};
`

const ErrorPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${rem(6)};
  align-self: flex-start;
  padding: ${rem(6)} ${rem(10)};
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${props => props.theme.palette.messaging.warning.lightBackground};
`

const Content = styled.div`
  display: grid;
  gap: ${rem(16)};
  padding: ${rem(16)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: minmax(0, 1fr) ${rem(280)};
    padding: ${rem(24)};
  }
`

const ArenaPanel = styled.div`
  min-height: clamp(${rem(420)}, 70dvh, ${rem(720)});
  border-radius: ${props => props.theme.borderRadius.S};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  overflow: hidden;

  igo-arena {
    display: block;
    width: 100%;
    min-height: clamp(${rem(420)}, 70dvh, ${rem(720)});
    --igo-map-height: clamp(${rem(420)}, 70dvh, ${rem(720)});
    --igo-map-width: 100%;
    --igo-legend-gap: 0.5rem;
  }
`

const SummaryPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
`

const SeatList = styled.ul`
  display: grid;
  gap: ${rem(8)};
  margin: 0;
  padding: 0;
  list-style: none;
`

const SeatItem = styled.li`
  display: grid;
  gap: ${rem(4)};
  padding: ${rem(12)};
  border-radius: ${props => props.theme.borderRadius.S};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

const ColourSwatch = styled.span<{ $bgColor?: string, $borderColor?: string }>`
  display: inline-block;
  width: ${rem(12)};
  height: ${rem(12)};
  margin-inline-end: ${rem(6)};
  border-radius: 50%;
  vertical-align: -1px;
  background-color: ${props => props.$bgColor ?? props.theme.palette.neutral.default.five};
  border: 1px solid ${props => props.$borderColor ?? props.theme.palette.neutral.default.four};
`

function getErrorLabel(intl: ReturnType<typeof useIntl>, error: ArenaErrorState) {
  switch (error.type) {
    case 'arena-api-unavailable':
      return intl.formatMessage({
        id: '_AJJfktgKzPjEEujOeKx',
        defaultMessage: 'Arena script loaded, but the Arena API was not available.',
        description: 'Error shown when the Ingresso Arena script loads without exposing the expected browser API',
      })
    case 'arena-create-failed':
      return intl.formatMessage({
        id: 'r_9PKLa6KxAUB3-k5JEo',
        defaultMessage: 'Arena could not start the seat map.',
        description: 'Error shown when the Ingresso Arena seat map cannot be created',
      })
    case 'event-failed':
      return intl.formatMessage({
        id: '-ODJG8XIuM-zmzMZmqsx',
        defaultMessage: 'Arena event failed: {eventName}',
        description: 'Error shown when Ingresso Arena emits a failure event',
      }, { eventName: error.eventName })
    case 'missing-trolley-token':
      return intl.formatMessage({
        id: 'BMpk4KKAZzlvYWJ_BAFQ',
        defaultMessage: 'We couldn’t prepare those seats for checkout. Please choose different seats and try again.',
        description: 'Customer-facing error shown when selected Ingresso Arena seats cannot be prepared for checkout',
      })
    case 'reserve-failed':
      switch (error.reservationFailureReason) {
        case 'leaves-single-seat':
          return intl.formatMessage({
            id: 'kaxmhfd07QcCg-be_1o7',
            defaultMessage: 'Those seats would leave a single seat unavailable. Please choose seats together and try again.',
            description: 'Customer-facing error shown when selected Ingresso Arena seats would leave a single seat unavailable',
          })
        case 'missing-transaction':
          return intl.formatMessage({
            id: '-3bkt_02hAECWFcBXoY3',
            defaultMessage: 'We couldn’t confirm your seat reservation. Please choose different seats and try again.',
            description: 'Customer-facing error shown when Ingresso Arena cannot confirm the seat reservation',
          })
        case 'seat-unavailable':
          return intl.formatMessage({
            id: 'dAQN20pKPTt19zmW9skw',
            defaultMessage: 'We couldn’t reserve one or more of those seats. Please choose different seats and try again.',
            description: 'Customer-facing error shown when one or more selected Ingresso Arena seats cannot be reserved',
          })
        case 'some-seats-unavailable':
          return intl.formatMessage({
            id: 'B6U28xEYnIVq1UPqhjNm',
            defaultMessage: 'Some of those seats could not be reserved. Please choose different seats and try again.',
            description: 'Customer-facing error shown when some selected Ingresso Arena seats cannot be reserved',
          })
        case 'reserve-failed':
        default:
          return intl.formatMessage({
            id: '4uFo60MiA7zqGhn-NLW4',
            defaultMessage: 'We couldn’t reserve those seats. Please choose different seats and try again.',
            description: 'Customer-facing error shown when selected Ingresso Arena seats cannot be reserved',
          })
      }
    case 'script-load-failed':
      return intl.formatMessage({
        id: 'XJMt-S1SewSdOx-tzLUP',
        defaultMessage: 'Arena could not load the seat map.',
        description: 'Error shown when the Ingresso Arena browser script cannot be loaded',
      })
    case 'selection-session-failed':
    default:
      return intl.formatMessage({
        id: 'hpHcAI-IXln_eZFC_kvw',
        defaultMessage: 'Arena could not create a selection session for this offer.',
        description: 'Error shown when the app cannot create an Ingresso Arena selection session',
      })
  }
}

function waitForTrolleyToken(resolverRef: React.MutableRefObject<((token: string) => void) | undefined>) {
  return new Promise<string | undefined>((resolve) => {
    const timeout = window.setTimeout(() => {
      if (resolverRef.current === resolveToken) resolverRef.current = undefined
      resolve(undefined)
    }, 5000)

    function resolveToken(token: string) {
      window.clearTimeout(timeout)
      resolve(token)
    }

    resolverRef.current = resolveToken
  })
}

function isReservationError(error: ArenaErrorState) {
  return error.type === 'missing-trolley-token' || error.type === 'reserve-failed'
}

function patchArenaSeatHoverMessages(root: ParentNode, restrictedViewLabel: string, seatMessages: Map<string, string>) {
  function clearWarningText(container: Element) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
    let node = walker.nextNode()

    while (node) {
      if (node.parentElement?.hasAttribute('data-le-arena-restricted-view-label')) {
        node = walker.nextNode()
        continue
      }

      if (node.nodeValue?.trim()) node.nodeValue = ''
      node = walker.nextNode()
    }
  }

  function getWarningContainer(icon: Element) {
    let element = icon.parentElement

    while (element && element !== root) {
      if (element.classList.contains('igo:bg-white') && element.classList.contains('igo:content-center')) return element
      element = element.parentElement
    }
  }

  root.querySelectorAll('acl-icon').forEach((icon) => {
    const warningContainer = getWarningContainer(icon)
    if (!warningContainer) return
    const warningContent = icon.parentElement ?? warningContainer

    const labelElement = warningContent.querySelector('[data-le-arena-restricted-view-label]') ?? document.createElement('span')
    labelElement.setAttribute('data-le-arena-restricted-view-label', 'true')
    if (labelElement.textContent !== restrictedViewLabel) labelElement.textContent = restrictedViewLabel

    clearWarningText(warningContainer)

    if (!labelElement.parentElement) warningContent.appendChild(labelElement)
  })

  if (seatMessages.size === 0) return

  root.querySelectorAll('.igo\\:flex-col').forEach((tooltipCard) => {
    if (!tooltipCard.classList.contains('igo:bg-white')) return

    const matchingMessage = Array.from(seatMessages.entries()).find(([label]) => tooltipCard.textContent?.includes(label))?.[1]
    const existingLabel = tooltipCard.querySelector('[data-le-arena-seat-message-label]')
    if (!matchingMessage) {
      existingLabel?.remove()
      return
    }

    const labelElement = existingLabel ?? document.createElement('span')
    labelElement.setAttribute('data-le-arena-seat-message-label', 'true')
    labelElement.classList.add('igo:block', 'igo:mt-2', 'igo:text-center')
    labelElement.textContent = matchingMessage
    if (!labelElement.parentElement) tooltipCard.appendChild(labelElement)
  })
}

function ArenaSeatSelection(props: Props) {
  const { apiBase, displayCurrencyCode, eventId, experienceId, experienceName, onSelectionComplete, options = defaultArenaOptions, perfId, scriptSrc = ARENA_SCRIPT_SRC, selectedTimeSlot, tyrBase } = props
  const intl = useIntl()
  const arenaElementRef = useRef<HTMLElement | null>(null)
  const arenaAppRef = useRef<ArenaApp | undefined>(undefined)
  const arenaSeatMessagesRef = useRef<Map<string, string>>(new Map())
  const displayCurrencyCodeRef = useRef(displayCurrencyCode)
  const selectedTimeSlotRef = useRef(selectedTimeSlot)
  const selectionSessionTokenRef = useRef<string | undefined>(undefined)
  const sendMethodRef = useRef<ArenaSendMethod | undefined>(undefined)
  const trolleyTokenRef = useRef<string | undefined>(undefined)
  const pendingTrolleyTokenResolverRef = useRef<((token: string) => void) | undefined>(undefined)
  const [currency, setCurrency] = useState<ArenaCurrency | undefined>()
  const [selectedSeats, setSelectedSeats] = useState<Array<ArenaSelectedSeat>>([])
  const [trolleyToken, setTrolleyToken] = useState<string | undefined>()
  const [status, setStatus] = useState<ArenaStatus>('idle')
  const [error, setError] = useState<ArenaErrorState | undefined>()
  const [arenaResetKey, setArenaResetKey] = useState(0)
  const { mutateAsync: createSelectionSession } = useCreateExperienceSelectionSession()
  displayCurrencyCodeRef.current = displayCurrencyCode
  selectedTimeSlotRef.current = selectedTimeSlot

  const handleArenaEvent = useCallback((eventName: ArenaEventName, payload: ArenaEventPayload) => {
    const update = getArenaEventStateUpdate(eventName, payload)
    if (update.status) setStatus(update.status)
    if ('error' in update) {
      setError(update.error ? { type: update.error, eventName: update.failedEventName } : undefined)
    }
    if ('currency' in update) setCurrency(update.currency)
    if (update.legend) {
      arenaAppRef.current?.setCustomLegend?.(getArenaCustomLegend(update.legend, selectedTimeSlotRef.current, displayCurrencyCodeRef.current))
    }
    if (update.seats) {
      arenaSeatMessagesRef.current = new Map(update.seats
        .filter(seat => seat.restrictedViewText && !seat.isRestrictedView)
        .map(seat => [seat.label ?? seat.seatId, seat.restrictedViewText!]))
    }
    if (update.selectedSeats) setSelectedSeats(update.selectedSeats)
    if ('sendMethod' in update) sendMethodRef.current = update.sendMethod
    if (update.shouldClearTrolleyToken) {
      trolleyTokenRef.current = undefined
      setTrolleyToken(undefined)
    }
    if ('trolleyToken' in update) {
      trolleyTokenRef.current = update.trolleyToken
      setTrolleyToken(update.trolleyToken)
      if (update.trolleyToken) {
        pendingTrolleyTokenResolverRef.current?.(update.trolleyToken)
        pendingTrolleyTokenResolverRef.current = undefined
      }
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    let arenaApp: ArenaApp | undefined
    const listeners = new Map<ArenaEventName, (payload: ArenaEventPayload) => void>()

    async function initialiseArena() {
      setStatus('creating-session')
      setError(undefined)
      setCurrency(undefined)
      setSelectedSeats([])
      selectionSessionTokenRef.current = undefined
      sendMethodRef.current = undefined
      trolleyTokenRef.current = undefined
      pendingTrolleyTokenResolverRef.current = undefined
      setTrolleyToken(undefined)
      let selectionSession
      try {
        selectionSession = await createSelectionSession(experienceId)
      } catch {
        if (!cancelled) {
          setStatus('failed')
          setError({ type: 'selection-session-failed' })
        }
        return
      }

      if (cancelled) return
      selectionSessionTokenRef.current = selectionSession.sessionToken

      setStatus('loading-script')
      try {
        await loadArenaScript(scriptSrc)
      } catch {
        if (!cancelled) {
          setStatus('failed')
          setError({ type: 'script-load-failed' })
        }
        return
      }

      if (cancelled) return

      if (!window.IngressoArena) {
        setStatus('failed')
        setError({ type: 'arena-api-unavailable' })
        return
      }

      setStatus('creating-arena')
      let app
      try {
        app = await window.IngressoArena.create({
          apiAuthToken: selectionSession.sessionToken,
          apiBase,
          eventId,
          perfId,
          tyrBase,
          options,
        })
      } catch {
        if (!cancelled) {
          setStatus('failed')
          setError({ type: 'arena-create-failed' })
        }
        return
      }

      if (cancelled) {
        app.destroy?.()
        return
      }

      arenaAppRef.current = app
      arenaApp = app
      arenaEvents.forEach((eventName) => {
        const listener = (payload: ArenaEventPayload) => handleArenaEvent(eventName, payload)
        listeners.set(eventName, listener)
        app.on(eventName, listener)
      })
      setStatus('ready')
    }

    initialiseArena().catch(() => {
      if (!cancelled) {
        setStatus('failed')
        setError({ type: 'selection-session-failed' })
      }
    })

    return () => {
      cancelled = true
      listeners.forEach((listener, eventName) => {
        arenaApp?.off?.(eventName, listener)
      })
      listeners.clear()
      arenaApp?.destroy?.()
      arenaAppRef.current = undefined
      selectionSessionTokenRef.current = undefined
      sendMethodRef.current = undefined
      pendingTrolleyTokenResolverRef.current = undefined
    }
  }, [apiBase, arenaResetKey, createSelectionSession, eventId, experienceId, handleArenaEvent, options, perfId, scriptSrc, tyrBase])

  const totalPrice = useMemo(() => getArenaDisplayTotalPrice(selectedSeats, selectedTimeSlot), [selectedSeats, selectedTimeSlot])
  const hasMatchedTickets = useMemo(() => areAllArenaSeatsMatchedToTickets(selectedSeats, selectedTimeSlot), [selectedSeats, selectedTimeSlot])
  const canContinue = selectedSeats.length > 0 && hasMatchedTickets && (status === 'ready' || status === 'reserved')
  const heading = intl.formatMessage({
    id: 'TlWxNkjpLr8x3z-5BdbE',
    defaultMessage: 'Select your seats',
    description: 'Heading for the Ingresso Arena seat selection component on the experience offer page',
  })
  const selectedSeatsHeading = intl.formatMessage({
    id: '5ha49ClT-KFRPjxKGTnG',
    defaultMessage: 'Selected seats',
    description: 'Heading for the selected seats summary beside the Ingresso Arena map',
  })
  const emptySelectionCopy = intl.formatMessage({
    id: '4GGf7QnUMf-ex4pP0Yqz',
    defaultMessage: 'Choose seats from the map to continue.',
    description: 'Empty-state copy shown before the customer chooses seats in the Ingresso Arena map',
  })
  const restrictedViewFallback = intl.formatMessage({
    id: 'KRBnKMohpp4B0QQY5Mi2',
    defaultMessage: 'Warning: this seat has a restricted view',
    description: 'Fallback label shown when an Arena selected seat is marked as restricted view without detail text',
  })
  const restrictedViewHoverLabel = intl.formatMessage({
    id: 'TIiNcd6AcVWfarEOs2gm',
    defaultMessage: 'Restricted view',
    description: 'Short warning label shown in the Ingresso Arena seat hover when a seat has a restricted view',
  })
  const totalLabel = intl.formatMessage({
    id: '5yGawpoHtVv7YuYDVxtp',
    defaultMessage: 'Total:',
    description: 'Label for the selected seats total price in the Ingresso Arena summary',
  })
  const reservationReadyMessage = intl.formatMessage({
    id: 'jK6UkYO18aZFC155Ijm0',
    defaultMessage: 'Seat reservation ready.',
    description: 'Message shown after Ingresso Arena returns a trolley token for the selected seats',
  })
  const continueLabel = intl.formatMessage({
    id: 'tby5Wm9vH3XBQ7lV5pRd',
    defaultMessage: 'Continue',
    description: 'Button label to continue after choosing seats in the Ingresso Arena component',
  })
  const handleReservationError = useCallback((nextError: ArenaErrorState) => {
    setStatus('failed')
    setError(nextError)
    showSnackbar(getErrorLabel(intl, nextError), 'critical')
    setArenaResetKey(key => key + 1)
  }, [intl])

  useEffect(() => {
    let observer: MutationObserver | undefined
    let attempts = 0
    let animationFrame: number | undefined
    let timeout: number | undefined

    function observeArenaShadowRoot() {
      const shadowRoot = arenaElementRef.current?.shadowRoot
      if (!shadowRoot) {
        attempts += 1
        if (attempts < 20) timeout = window.setTimeout(observeArenaShadowRoot, 250)
        return
      }

      const patchRestrictedViewWarnings = () => {
        if (animationFrame) return

        animationFrame = window.requestAnimationFrame(() => {
          animationFrame = undefined
          patchArenaSeatHoverMessages(shadowRoot, restrictedViewHoverLabel, arenaSeatMessagesRef.current)
        })
      }
      patchRestrictedViewWarnings()

      observer = new MutationObserver(patchRestrictedViewWarnings)
      observer.observe(shadowRoot, { childList: true, characterData: true, subtree: true })
    }

    observeArenaShadowRoot()

    return () => {
      observer?.disconnect()
      if (animationFrame) window.cancelAnimationFrame(animationFrame)
      if (timeout) window.clearTimeout(timeout)
    }
  }, [restrictedViewHoverLabel, status])

  const handleContinue = useCallback(async() => {
    if (!arenaAppRef.current || selectedSeats.length === 0) return

    setStatus('reserving')
    setError(undefined)
    const reservedSeats = selectedSeats

    try {
      let nextTrolleyToken: string | undefined
      let shouldValidateTrolleyReservation = false

      if (hasMixedArenaReservableSeatGroups(reservedSeats)) {
        if (!selectionSessionTokenRef.current || !sendMethodRef.current) {
          handleReservationError({ type: 'reserve-failed' })
          return
        }

        nextTrolleyToken = await createArenaTrolleyToken({
          apiAuthToken: selectionSessionTokenRef.current,
          apiBase,
          perfId,
          selectedSeats: reservedSeats,
          sendMethod: sendMethodRef.current,
        })
        shouldValidateTrolleyReservation = true
      } else {
        const reserveResult = await arenaAppRef.current.reserveSeats?.()
        if (reserveResult && typeof reserveResult !== 'string' && !hasValidArenaReserveResult(reserveResult)) {
          handleReservationError({
            type: 'reserve-failed',
            reservationFailureReason: getArenaReservationFailureReason(reserveResult),
          })
          return
        }
        nextTrolleyToken = getTrolleyToken(reserveResult) ?? trolleyTokenRef.current ?? await waitForTrolleyToken(pendingTrolleyTokenResolverRef)
        shouldValidateTrolleyReservation = !reserveResult || typeof reserveResult === 'string'
      }

      if (!nextTrolleyToken) {
        handleReservationError({ type: 'missing-trolley-token' })
        return
      }

      if (shouldValidateTrolleyReservation && (!selectionSessionTokenRef.current || !sendMethodRef.current)) {
        handleReservationError({ type: 'reserve-failed' })
        return
      }

      if (shouldValidateTrolleyReservation) {
        const apiAuthToken = selectionSessionTokenRef.current!
        const sendMethod = sendMethodRef.current!
        const reservationValidation = await validateArenaTrolleyReservation({
          apiAuthToken,
          apiBase,
          sendMethod,
          trolleyToken: nextTrolleyToken,
        })
        if (!reservationValidation.isValid) {
          handleReservationError({ type: 'reserve-failed', reservationFailureReason: reservationValidation.failureReason })
          return
        }
      }

      trolleyTokenRef.current = nextTrolleyToken
      setTrolleyToken(nextTrolleyToken)
      setStatus('reserved')
      onSelectionComplete({
        currency,
        selectedSeats: reservedSeats,
        trolleyToken: nextTrolleyToken,
        totalPrice,
      })
    } catch {
      handleReservationError({ type: 'reserve-failed' })
    }
  }, [apiBase, currency, handleReservationError, onSelectionComplete, perfId, selectedSeats, totalPrice])

  return (
    <Shell data-testid="arena-seat-selection">
      <Header>
        <HeaderCopy>
          <Heading variant="heading4">{heading}</Heading>
          <BodyText variant="medium" colour="neutral-two">
            {experienceName}
          </BodyText>
        </HeaderCopy>
        {error && !isReservationError(error) && <ErrorPill>
          <LineExclamationCircleIcon colour="warning" size="S" />
          <BodyText variant="small" weight="bold">
            {getErrorLabel(intl, error)}
          </BodyText>
        </ErrorPill>}
      </Header>
      <Content>
        <ArenaPanel>
          {React.createElement('igo-arena', { key: arenaResetKey, ref: arenaElementRef })}
        </ArenaPanel>
        <SummaryPanel>
          <VerticalSpacer gap={12}>
            <Heading variant="heading5">{selectedSeatsHeading}</Heading>
            {selectedSeats.length === 0 ? (
              <BodyText variant="medium" colour="neutral-two">
                {emptySelectionCopy}
              </BodyText>
            ) : (
              <SeatList>
                {selectedSeats.map((seat) => {
                  const seatPrice = getArenaDisplaySeatPrice(seat, selectedTimeSlot)
                  return (
                    <SeatItem key={seat.uuid ?? seat.seatId}>
                      <BodyText variant="medium" weight="bold">
                        <ColourSwatch $bgColor={seat.bgColor} $borderColor={seat.borderColor} />
                        {seat.label ?? seat.seatId}
                      </BodyText>
                      <BodyText variant="small" colour="neutral-two">
                        {[seat.ticketTypeDesc, seat.priceBandCode, seat.seatBlock?.desc].filter(Boolean).join(' · ')}
                      </BodyText>
                      {seatPrice !== undefined && (
                        <BodyText variant="medium">
                          <FormatCurrency value={seatPrice} currency={displayCurrencyCode} />
                        </BodyText>
                      )}
                      {seat.isRestrictedView && (
                        <BodyText variant="small" colour="critical">
                          {restrictedViewFallback}
                        </BodyText>
                      )}
                    </SeatItem>
                  )
                })}
              </SeatList>
            )}
          </VerticalSpacer>

          <VerticalSpacer gap={8}>
            <BodyText variant="medium" weight="bold">
              {totalLabel} <FormatCurrency value={totalPrice} currency={displayCurrencyCode} />
            </BodyText>
            {trolleyToken && (
              <BodyText variant="small" colour="neutral-two">
                {reservationReadyMessage}
              </BodyText>
            )}
            <TextButton kind="primary" fit="full-width" disabled={!canContinue} onClick={handleContinue}>
              {continueLabel}
            </TextButton>
          </VerticalSpacer>
        </SummaryPanel>
      </Content>
    </Shell>
  )
}

export default React.memo(ArenaSeatSelection)
